import { ActionBar } from "$components/action-bar/action-bar";
import { useRegisterActionBar } from "$components/action-bar/action-bar-offset";
import type { ActionItem } from "$components/action-bar/action-item";
import {
  HistoryIcon,
  MoveIcon,
  StockRequestsIcon,
  StockTakeIcon,
} from "$components/icons";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { useUser } from "$global-providers/user-provider";
import {
  hasMakeInventoryRequestPermission,
  hasStockTakeHistoryPermission,
  hasStockTakePermission,
  hasUpdateLotPermissions,
} from "$routes/inventory/_lib/permissions";
import {
  linkToBulkReceive,
  linkToBulkStocktake,
  linkToInitiateStockTake,
  linkToInventoryAudit,
  linkToInventoryMoveRequests,
  linkToStockTakeHistory,
} from "$routes/inventory/links";
import {
  CloudUpload as BulkReceiveIcon,
  CloudDownload as BulkStocktakeIcon,
  LocalShipping as ReceiveIcon,
} from "@material-ui/icons";
import { Role } from "elephant-web/gql";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  useInventoryTableFilterContext,
  useMoveDrawerContext,
  useReceiveDrawerContext,
} from "./inventory-context";

/**
 * The inventory overview's role-gated actions, listed directly in the bottom
 * action bar (previously collapsed behind an "Actions" chevron menu).
 * Receive/Move open their side drawers; the rest navigate. The primary
 * "Receive" leads the inline buttons; the lower-traffic Stocktake History and
 * Audit live in the "More actions" overflow.
 */
export const InventoryActionBar = (): JSX.Element | null => {
  const { role } = useUser();
  const { locationId } = useInventoryTableFilterContext();
  const { openMenu: openReceiveDrawer, isOpen: isReceiveDrawerOpen } =
    useReceiveDrawerContext();
  const { openMenu: openMoveDrawer, isOpen: isMoveDrawerOpen } =
    useMoveDrawerContext();
  const navigate = useNavigate();
  const [bulkReceiveFlag] = useFeatureFlag(featureFlags.inventory.bulkReceive);
  const [bulkStocktakeFlag] = useFeatureFlag(
    featureFlags.inventory.bulkStocktake,
  );
  const actionBarRef = useRef<HTMLElement>(null);
  useRegisterActionBar(actionBarRef);

  const actions: ActionItem[] = [
    ...(hasUpdateLotPermissions(role)
      ? [
          {
            id: "inventory.actions.receive",
            defaultMessage: "Receive",
            variant: "primary" as const,
            icon: <ReceiveIcon />,
            onClick: () => openReceiveDrawer(),
            testId: "receiveDrawerTriggerButton",
          },
          {
            id: "inventory.actions.move",
            defaultMessage: "Move",
            icon: <MoveIcon />,
            onClick: () => openMoveDrawer(),
          },
        ]
      : []),
    ...(hasUpdateLotPermissions(role) && bulkReceiveFlag
      ? [
          {
            id: "inventory.actions.bulkReceive",
            defaultMessage: "Bulk receive",
            icon: <BulkReceiveIcon />,
            onClick: () => navigate(linkToBulkReceive()),
            testId: "bulkReceiveMenuItem",
          },
        ]
      : []),
    ...(hasMakeInventoryRequestPermission(role)
      ? [
          {
            id: "inventory.actions.requests",
            defaultMessage: "Requests",
            icon: <StockRequestsIcon />,
            onClick: () => navigate(linkToInventoryMoveRequests()),
          },
        ]
      : []),
    ...(hasStockTakePermission(role)
      ? [
          {
            id: "inventory.actions.stockTake",
            defaultMessage: "Start Stocktake",
            icon: <StockTakeIcon />,
            onClick: () => navigate(linkToInitiateStockTake(locationId)),
          },
        ]
      : []),
    ...(hasStockTakePermission(role) && bulkStocktakeFlag
      ? [
          {
            id: "inventory.actions.bulkStocktake",
            defaultMessage: "Bulk stocktake",
            icon: <BulkStocktakeIcon />,
            onClick: () => navigate(linkToBulkStocktake()),
            testId: "bulkStocktakeMenuItem",
          },
        ]
      : []),
  ];

  const moreActions: ActionItem[] = [
    ...(hasStockTakeHistoryPermission(role)
      ? [
          {
            id: "inventory.actions.stocktakeHistory",
            defaultMessage: "Stocktake History",
            icon: <HistoryIcon />,
            onClick: () => navigate(linkToStockTakeHistory()),
          },
        ]
      : []),
    ...([Role.Clinic, Role.Pharmacist].includes(role)
      ? [
          {
            id: "inventory.actions.audit",
            defaultMessage: "Audit",
            icon: <HistoryIcon />,
            onClick: () => navigate(linkToInventoryAudit()),
          },
        ]
      : []),
  ];

  // While a side drawer is open it owns the screen's actions (and its own
  // submit lives at the bottom of the drawer), so the page-level bar — which
  // sits above the drawer — steps aside to avoid covering it.
  if (isReceiveDrawerOpen || isMoveDrawerOpen) {
    return null;
  }

  if (actions.length === 0 && moreActions.length === 0) {
    return null;
  }

  return (
    <ActionBar
      actions={actions}
      moreActionsItems={moreActions}
      containerRef={actionBarRef}
    />
  );
};
